import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute, EngineerRoute, PlatformProtectedRoute, PermissionRoute } from '@/components/auth/ProtectedRoute';
import { PageLoading } from '@/components/ui/loading';
import { useAuth } from '@/contexts/AuthContext';
import PrivacyPolicy from '@/components/common/PrivacyPolicy';

import LoginPage from '@/pages/auth/LoginPage';
import ChangePasswordPage from '@/pages/auth/ChangePasswordPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import UnauthorizedPage from '@/pages/auth/UnauthorizedPage';
import NotFoundPage from '@/pages/NotFoundPage';
import DashboardPage from '@/pages/DashboardPage';
import RequirementsListPage from '@/pages/requirements/RequirementsListPage';
import RequirementsFormPage from '@/pages/requirements/RequirementsFormPage';
import RequirementsDetailPage from '@/pages/requirements/RequirementsDetailPage';
import PendingApprovalsPage from '@/pages/requirements/PendingApprovalsPage';
import ReportsPage from '@/pages/reports/ReportsPage';
import NotificationsPage from '@/pages/notifications/NotificationsPage';
import ProfilePage from '@/pages/ProfilePage';
import UsersListPage from '@/pages/admin/UsersListPage';
import UserFormPage from '@/pages/admin/UserFormPage';
import ProjectsListPage from '@/pages/admin/ProjectsListPage';
import ProjectFormPage from '@/pages/admin/ProjectFormPage';
import WorkerCategoriesListPage from '@/pages/admin/WorkerCategoriesListPage';
import UnitsListPage from '@/pages/admin/UnitsListPage';
import AuditLogsPage from '@/pages/admin/AuditLogsPage';
import SettingsPage from '@/pages/admin/SettingsPage';
import ActualStatusListPage from '@/pages/actuals/ActualStatusListPage';
import ActualStatusFormPage from '@/pages/actuals/ActualStatusFormPage';
import ActualStatusDetailPage from '@/pages/actuals/ActualStatusDetailPage';
import BlocksListPage from '@/pages/admin/BlocksListPage';
import ActivitiesListPage from '@/pages/admin/ActivitiesListPage';
import RateCardPage from '@/pages/admin/RateCardPage';
import RolesListPage from '@/pages/admin/RolesListPage';
import RoleFormPage from '@/pages/admin/RoleFormPage';

import { PlatformLayout } from '@/components/layout/PlatformLayout';
import { PlatformAuthProvider } from '@/contexts/PlatformAuthContext';
import PlatformLoginPage from '@/pages/platform/PlatformLoginPage';
import PlatformChangePasswordPage from '@/pages/platform/PlatformChangePasswordPage';
import PlatformDashboardPage from '@/pages/platform/PlatformDashboardPage';
import OrganizationsPage from '@/pages/platform/OrganizationsPage';
import OrganizationFormPagePlatform from '@/pages/platform/OrganizationFormPage';
import OrganizationDetailPage from '@/pages/platform/OrganizationDetailPage';
import PlatformAuditLogsPage from '@/pages/platform/PlatformAuditLogsPage';
import PlatformProfilePage from '@/pages/platform/PlatformProfilePage';
import SupportDashboardPage from '@/pages/platform/SupportDashboardPage';

export default function App() {
  const { loading } = useAuth();

  if (loading) return <PageLoading />;

  return (
    <Routes>
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      <Route path="/super-admin" element={<PlatformAuthProvider><Outlet /></PlatformAuthProvider>}>
        <Route path="login" element={<PlatformLoginPage />} />
        <Route path="change-password" element={<PlatformChangePasswordPage />} />
        <Route element={<PlatformProtectedRoute />}>
          <Route element={<PlatformLayout />}>
            <Route index element={<PlatformDashboardPage />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="organizations/new" element={<OrganizationFormPagePlatform />} />
            <Route path="organizations/:id" element={<OrganizationDetailPage />} />
            <Route path="organizations/:id/edit" element={<OrganizationFormPagePlatform />} />
            <Route path="audit-logs" element={<PlatformAuditLogsPage />} />
            <Route path="profile" element={<PlatformProfilePage />} />
            <Route path="support" element={<SupportDashboardPage />} />
          </Route>
        </Route>
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/notifications" element={<NotificationsPage />} />

          <Route path="/requirements" element={<RequirementsListPage />} />
          <Route element={<EngineerRoute />}>
            <Route path="/requirements/new" element={<RequirementsFormPage />} />
            <Route path="/actuals/new" element={<ActualStatusFormPage />} />
          </Route>
          <Route path="/requirements/:id" element={<RequirementsDetailPage />} />
          <Route path="/requirements/:id/edit" element={<RequirementsFormPage />} />
          <Route path="/approvals" element={<PendingApprovalsPage />} />

          <Route path="/actuals" element={<ActualStatusListPage />} />
          <Route path="/actuals/:id" element={<ActualStatusDetailPage />} />
          <Route path="/actuals/:id/edit" element={<ActualStatusFormPage />} />

          <Route element={<PermissionRoute anyOf={['rate_cards:view']} />}>
            <Route path="/rate-card" element={<RateCardPage />} />
          </Route>

          <Route element={<PermissionRoute anyOf={['reports:view']} />}>
            <Route path="/reports" element={<ReportsPage />} />
          </Route>

          <Route element={<PermissionRoute anyOf={['users:view']} />}>
            <Route path="/admin/users" element={<UsersListPage />} />
          </Route>
          <Route element={<PermissionRoute anyOf={['users:create']} />}>
            <Route path="/admin/users/new" element={<UserFormPage />} />
          </Route>
          <Route element={<PermissionRoute anyOf={['users:update']} />}>
            <Route path="/admin/users/:id/edit" element={<UserFormPage />} />
          </Route>
          <Route element={<PermissionRoute anyOf={['roles:view']} />}>
            <Route path="/admin/roles" element={<RolesListPage />} />
            <Route path="/admin/roles/:id/edit" element={<RoleFormPage />} />
          </Route>
          <Route element={<PermissionRoute anyOf={['roles:create']} />}>
            <Route path="/admin/roles/new" element={<RoleFormPage />} />
          </Route>
          <Route element={<PermissionRoute anyOf={['projects:view']} />}>
            <Route path="/admin/projects" element={<ProjectsListPage />} />
          </Route>
          <Route element={<PermissionRoute anyOf={['projects:manage']} />}>
            <Route path="/admin/projects/new" element={<ProjectFormPage />} />
            <Route path="/admin/projects/:id/edit" element={<ProjectFormPage />} />
          </Route>
          <Route path="/admin/categories" element={<Navigate to="/admin/worker-categories" replace />} />
          <Route element={<PermissionRoute anyOf={['worker_categories:view']} />}>
            <Route path="/admin/worker-categories" element={<WorkerCategoriesListPage />} />
          </Route>
          <Route element={<PermissionRoute anyOf={['units:view']} />}>
            <Route path="/admin/units" element={<UnitsListPage />} />
          </Route>
          <Route element={<PermissionRoute anyOf={['audit:view']} />}>
            <Route path="/admin/audit" element={<AuditLogsPage />} />
          </Route>
          <Route element={<PermissionRoute anyOf={['settings:view']} />}>
            <Route path="/admin/settings" element={<SettingsPage />} />
          </Route>
          <Route element={<PermissionRoute anyOf={['blocks:view']} />}>
            <Route path="/admin/blocks" element={<BlocksListPage />} />
          </Route>
          <Route element={<PermissionRoute anyOf={['activities:view']} />}>
            <Route path="/admin/activities" element={<ActivitiesListPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
